import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Pagination as PaginationMeta } from '@/types/api';
import { Button } from '@/components/common/Button';

interface PaginationProps {
  pagination: PaginationMeta | undefined;
  onPageChange: (page: number) => void;
  onSizeChange?: (size: number) => void;
}

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Driven entirely by the server's pagination metadata rather than by counting
 * rows on screen, so the controls agree with what the API actually returned.
 */
export function Pagination({ pagination, onPageChange, onSizeChange }: PaginationProps) {
  if (pagination === undefined || pagination.totalElements === 0) {
    return null;
  }

  const { page, size, totalElements, totalPages, first, last } = pagination;
  const from = page * size + 1;
  const to = Math.min((page + 1) * size, totalElements);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
      <p className="text-sm text-muted">
        <span className="font-medium text-ink tabular-nums">{from}</span>–
        <span className="font-medium text-ink tabular-nums">{to}</span> of{' '}
        <span className="font-medium text-ink tabular-nums">{totalElements}</span>
      </p>

      <div className="flex items-center gap-3">
        {onSizeChange !== undefined && (
          <label className="flex items-center gap-2 text-sm text-muted">
            <span className="sr-only">Rows per page</span>
            <select
              value={size}
              onChange={(event) => onSizeChange(Number(event.target.value))}
              className="rounded-lg border border-line bg-card py-1.5 pl-2.5 pr-7 text-sm text-ink transition-colors hover:border-line-strong focus:border-accent focus:outline-none"
            >
              {PAGE_SIZES.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="icon"
            disabled={first}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="px-2 text-sm tabular-nums text-muted">
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="secondary"
            size="icon"
            disabled={last}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
